import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import Image from 'next/image';
import styled, { css } from 'styled-components';

import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import { ImageComponent } from 'qualification/schema/questions/utils';
import CheckmarkIcon from 'shared/svg/CheckmarkIcon';

const imageSizeByOrientation = {
  horizontal: 80,
  vertical: 320,
};

type Orientation = 'horizontal' | 'vertical';

export type CardSelectOption<T> =
  | {
      value: T;
      subtitle?: string;
      label: string;
      image: string;
    }
  | {
      value: T;
      subtitle?: string;
      label: string;
      imageComponent: ImageComponent;
    };

type Props<T> = {
  option: CardSelectOption<T>;
  onClick: (value: T) => void;
  /**
   * The selected value
   */
  selected?: T | T[];
  multi?: boolean;
  orientation: Orientation;
};

const CardSelectItem = <T extends string>({
  option,
  onClick,
  selected,
  multi,
  orientation,
}: Props<T>) => {
  const isSelected = React.useMemo(() => {
    if (selected === undefined) return false;
    if (multi) return selected.includes(option.value);
    return selected === option.value;
  }, [selected, multi, option.value]);

  const isMobile = useBreakpointDown('xs');
  const imageSize =
    orientation === 'horizontal' || isMobile
      ? imageSizeByOrientation.horizontal
      : imageSizeByOrientation.vertical;

  return (
    <StyledCardSelectItem
      onClick={() => onClick(option.value)}
      $selected={isSelected}
      $orientation={orientation}
    >
      {'image' in option && option.image && (
        <CardSelectItemImage $orientation={orientation}>
          <Image
            alt=""
            src={option.image}
            width={imageSize}
            height={imageSize}
          />
        </CardSelectItemImage>
      )}
      {'imageComponent' in option && option.imageComponent && (
        <>
          {
            <option.imageComponent
              alt={option.label}
              width={imageSize}
              height={imageSize}
            />
          }
        </>
      )}

      <CardSelectItemTypography $orientation={orientation}>
        <LabelTypography variant="body1" px={1}>
          {option.label}
        </LabelTypography>
        <Typography variant="caption">{option.subtitle}</Typography>
      </CardSelectItemTypography>
      {orientation === 'horizontal' && (
        <Box ml="auto" display="flex" alignItems="center" mr={2}>
          <CheckmarkIcon checked={isSelected} />
        </Box>
      )}
    </StyledCardSelectItem>
  );
};

interface StyledCardSelectItemProps {
  $orientation: Orientation;
  $selected: boolean;
}
const StyledCardSelectItem = styled.div<StyledCardSelectItemProps>`
  cursor: pointer;
  display: grid;
  background-color: white;
  justify-items: ${({ $orientation }) =>
    $orientation === 'horizontal' ? 'start' : 'center'};

  grid-template-columns: ${({ $orientation }) =>
    $orientation === 'horizontal' ? 'auto 1fr auto' : '1fr'};

  align-items: center;
  white-space: nowrap;
  border-width: 2px;
  border-style: solid;
  border-color: ${(props) =>
    props.$selected
      ? props.theme.palette.secondary.light
      : props.theme.palette.grey[200]};
  border-radius: ${(props) => props.theme.spacing(0.5)}px;
  box-shadow: ${(props) =>
    props.$selected ? '0 4px 23px 0px rgba(156, 186, 108, 0.2)' : 'none'};
  overflow: hidden;
  transition: ${(props) =>
    props.theme.transitions.create(['box-shadow', 'border-color'], {
      duration: '.2s',
    })};

  ${(props) =>
    !props.$selected &&
    css`
      &:hover {
        box-shadow: 0 4px 40px 0 rgba(62, 41, 29, 0.08);
      }
    `}

  ${(props) => props.theme.breakpoints.down('xs')} {
    flex-direction: row;
  }
`;

const CardSelectItemImage = styled.div<{ $orientation: Orientation }>`
  display: flex;
  & > div {
    display: block !important;
  }
  ${(props) =>
    props.$orientation === 'horizontal' &&
    css`
      flex-basis: ${imageSizeByOrientation.horizontal}px;
      flex-grow: 0;
      flex-shrink: 0;
    `}
  ${(props) => props.theme.breakpoints.down('xs')} {
    flex-basis: ${imageSizeByOrientation.horizontal}px;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

const CardSelectItemTypography = styled.div<{ $orientation: Orientation }>`
  text-overflow: ellipsis;
  overflow: hidden;
  > * {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  max-width: 100%;

  padding-left: ${({ $orientation, theme }) =>
    $orientation === 'horizontal' ? theme.spacing(2) : theme.spacing(0)}px;
  padding-top: ${(props) =>
    props.$orientation === 'horizontal'
      ? props.theme.spacing(0)
      : props.theme.spacing(2)}px;
  padding-bottom: ${(props) =>
    props.$orientation === 'horizontal'
      ? props.theme.spacing(0)
      : props.theme.spacing(2)}px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    flex-direction: row;
    padding-left: ${(props) => props.theme.spacing(2)}px;
    text-align: start;
  }
`;

const LabelTypography = styled(Typography)`
  white-space: normal;
  text-align: start;
`;

export default CardSelectItem;
